import { action, flow, observable } from 'mobx';
import { apiState, apiResponse } from '../../Api';
import { RootStore } from '../createStore';
import { MonografiaModel, PalavrasChave } from './Model';
import { AlunoModel } from '../Aluno';
import {
  fetchMonografiaDoAlunoAtual,
  fetchMonografiaDoAluno,
  editarMonografiaDoAluno,
  fetchMonografiasQuePossuemDeposito,
  fetchProfessorMonografias,
  fetchProfessorMonografiasJaApresentadas,
  fetchMonografiaDoOrientando,
  editarTituloMonografiaDoAluno,
  editarSituacaoDepositoDoAluno,
  editarOrientadorMonografiaDoAluno,
  editarNotaMonografiaDoAluno
} from './Service';
import { ValidationError } from '../IValidator';
import { ProfessorModel } from '../Professor';

export class MonografiaStore {
  @observable public monografia: MonografiaModel = new MonografiaModel();
  @observable public monografias: Array<MonografiaModel> = [];
  @observable public todasMonografias: Array<MonografiaModel> = [];

  @observable public professor: ProfessorModel = new ProfessorModel();

  @observable monografiaState: apiState = apiState.loading;
  @observable monografiaListState: apiState = apiState.loading;
  @observable todasMonografiasListState: apiState = apiState.loading;
  @observable monografiaValidationError: ValidationError<{ palavrasChave: Array<PalavrasChave>, titulo: string }>
  //@observable public nota: number
  @observable public notaOrientador: number
  @observable public notaConvidado: number
  @observable public observacao: string
  @observable public autorizacaoProfessor: boolean

  @observable monografiaListQuery = {
    search: '',
    page: 1,
    pageSize: 10
  };

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    this.monografia.aluno = new AlunoModel();
  }

  @action
  update = (object: string, field: string, value: any) => {
    this[object][field] = value;
  };

  @action
    updateAttribute(name: string, value: any) {
        this[name] = value
    }

  @action
  setValidationError = (
    validationError: ValidationError<{ palavrasChave: Array<PalavrasChave>, titulo: string }>
  ) => {
    this.monografiaValidationError = validationError;
  };

  getMonografiaDoAlunoAtual = flow(function* (this: MonografiaStore) {
    this.monografiaState = apiState.loading;

    const tcc: apiResponse<MonografiaModel> = yield fetchMonografiaDoAlunoAtual();

    this.monografiaState = tcc.status;
    this.monografia = tcc.data;

    if (this.monografia && this.monografia.deposito) {
      this.rootStore.depositoStore.deposito = this.monografia.deposito;
      this.rootStore.depositoStore.file = this.monografia.deposito.conteudoDoArquivo;
    }
  });

  getMonografiaDoAluno = flow(function* (this: MonografiaStore, alunoId: number) {
    this.monografiaState = apiState.loading;

    const tcc: apiResponse<MonografiaModel> = yield fetchMonografiaDoAluno(alunoId);

    this.monografiaState = tcc.status;
    this.monografia = tcc.data;

    if (this.monografia) {
      this.professor = this.monografia.professor
    }

    if (this.monografia && this.monografia.deposito) {
      this.rootStore.depositoStore.deposito = this.monografia.deposito;
      this.rootStore.depositoStore.file = this.monografia.deposito.conteudoDoArquivo;
    }
  });

  editarMonografiaDoAluno = flow(function* (this: MonografiaStore) {
    this.monografiaState = apiState.loading;

    const result: apiResponse<undefined> = yield editarMonografiaDoAluno(
      this.monografia.id,
      this.monografia.titulo,
      this.monografia.palavrasChave.map(pc => pc.palavraChave),
      this.monografia.autorizacaoAluno,
    );
    this.monografiaState = result.status;
  });

  editarTituloMonografiaDoAluno = flow(function* (this: MonografiaStore) {
    this.monografiaState = apiState.loading;

    const result: apiResponse<undefined> = yield editarTituloMonografiaDoAluno(
      this.monografia.id,
      this.monografia.titulo,
    );
    this.monografiaState = result.status;
  });

  editarOrientadorMonografiaDoAluno = flow(function* (this: MonografiaStore, monografiaId: number, professorId: number) {
    this.monografiaState = apiState.loading;

    const result: apiResponse<undefined> = yield editarOrientadorMonografiaDoAluno(
      monografiaId,professorId
    );
    this.monografiaState = result.status;
  });

  editarNotaMonografiaDoAluno = flow(function* (this: MonografiaStore, monografiaId: number,
    notaO: number|undefined, notaC: number|undefined, obs: string|undefined, aut: boolean|undefined) {
    this.monografiaState = apiState.loading;

    const monografia = {
      notaOrientador: this.notaOrientador!==undefined?this.notaOrientador:(notaO!==undefined?notaO:0),
      notaConvidado: this.notaConvidado!==undefined?this.notaConvidado:(notaC!==undefined?notaC:0),
      observacao: (this.observacao!==undefined&&this.observacao!==null)?this.observacao:((obs!==undefined&&obs!==null)?obs:''),
      autorizacaoProfessor: (this.autorizacaoProfessor!==undefined&&this.autorizacaoProfessor!==null)?this.autorizacaoProfessor:((aut!==undefined&&aut!==null)?aut:false)
    }
    const result: apiResponse<undefined> = yield editarNotaMonografiaDoAluno(
      monografia, monografiaId);

    this.monografiaState = result.status;
  });

  editarSituacaoDepositoDoAluno = flow(function* (this: MonografiaStore) {
    this.monografiaState = apiState.loading;

    const result: apiResponse<undefined> = yield editarSituacaoDepositoDoAluno(
      this.monografia.id,
    );
    this.monografiaState = result.status;
  });

  getMonografiaComDepositoDoOrientando = flow(function* (
    this: MonografiaStore,
    monografiaId: number
  ) {
    this.monografiaState = apiState.loading;

    const monografia = yield fetchMonografiaDoOrientando(monografiaId);

    this.monografiaState = monografia.status;
    this.monografia = monografia.data;

    if (this.monografia) {
      this.professor = this.monografia.professor
    }

    if (this.monografia.deposito) {
      this.rootStore.depositoStore.deposito = this.monografia.deposito;
      this.rootStore.depositoStore.file = this.monografia.deposito.conteudoDoArquivo;
    }
  });

  getMonografiasQuePossuemDeposito = flow(function* (
    this: MonografiaStore,
    concat: boolean = false
  ) {
    this.monografiaListState = apiState.loading;

    const { page, pageSize, search } = this.monografiaListQuery;

    const monografias = yield fetchMonografiasQuePossuemDeposito(
      page,
      pageSize,
      search
    );

    this.monografiaListState = monografias.status;

    this.monografias = concat
      ? this.monografias.concat(monografias.data)
      : monografias.data;
  });

  getMonografiasFromProfessor = flow(function* (this: MonografiaStore) {
    this.monografiaListState = apiState.loading;

    const fetchMonografias = yield fetchProfessorMonografias();

    this.monografiaListState = fetchMonografias.status;
    this.monografias = fetchMonografias.data;
  });

  getMonografiasJaApresentadas = flow(function* (this: MonografiaStore) {
    this.todasMonografiasListState = apiState.loading;

    const fetchTodasMonografias = yield fetchProfessorMonografiasJaApresentadas();

    this.todasMonografiasListState = fetchTodasMonografias.status;
    this.todasMonografias = fetchTodasMonografias.data;
  });
}
